import { format } from 'date-fns';

import type { FormField } from '@/types/formSchema';
import { parseDateFromStorage } from '@/utils/date';

/**
 * Format a date suggestion value for display.
 * Accepts backend/legacy MM-dd-yyyy and canonical yyyy-MM-dd, renders as MM/DD/YYYY.
 */
function formatDateSuggestion(suggestion: any): string {
  try {
    const date = parseDateFromStorage(String(suggestion));
    if (!date || isNaN(date.getTime())) {
      return String(suggestion);
    }
    return format(date, 'MM/dd/yyyy');
  } catch {
    return String(suggestion);
  }
}

/**
 * Format a select suggestion value by mapping to label
 */
function formatSelectSuggestion(suggestion: any, field: FormField): string {
  if (field.options) {
    const option = field.options.find(opt => opt.value === suggestion);
    if (option) {
      return option.label;
    }
  }
  return String(suggestion);
}

/**
 * Format a multi-select suggestion value by joining labels
 */
function formatMultiSelectSuggestion(suggestion: any, field: FormField): string {
  if (Array.isArray(suggestion) && field.options) {
    const labels = suggestion
      .map(value => {
        const option = field.options?.find(opt => opt.value === value);
        return option?.label || String(value);
      })
      .filter(Boolean);
    return labels.length > 0 ? labels.join(', ') : '';
  }
  return Array.isArray(suggestion) ? suggestion.join(', ') : String(suggestion);
}

/**
 * Format an array suggestion value by showing count
 */
function formatArraySuggestion(suggestion: any, field: FormField): string {
  if (Array.isArray(suggestion)) {
    const count = suggestion.length;
    const itemLabel = field.item_label || 'item';
    return `${count} ${count === 1 ? itemLabel : `${itemLabel}s`}`;
  }
  return String(suggestion);
}

/**
 * Format an object suggestion value by showing field count
 */
function formatObjectSuggestion(suggestion: any): string {
  if (typeof suggestion === 'object' && suggestion !== null) {
    const keys = Object.keys(suggestion);
    return `${keys.length} ${keys.length === 1 ? 'field' : 'fields'}`;
  }
  return String(suggestion);
}

/**
 * Format a numeric suggestion value
 */
function formatNumericSuggestion(suggestion: any): string {
  const num = Number(suggestion);
  return isNaN(num) ? String(suggestion) : String(num);
}

/**
 * Format a boolean suggestion value
 */
function formatBooleanSuggestion(suggestion: any): string {
  return suggestion ? 'Yes' : 'No';
}

/**
 * Format a generic string suggestion value
 */
function formatStringSuggestion(suggestion: any): string {
  return String(suggestion);
}

// Formatter map for different field types
type FormatterFunction = (suggestion: any, field: FormField) => string;

const formatters: Record<string, FormatterFunction> = {
  date: formatDateSuggestion,
  dob: formatDateSuggestion, // DOB uses same formatting as date
  select: formatSelectSuggestion,
  multi_select: formatMultiSelectSuggestion,
  checkbox: formatBooleanSuggestion,
  phone: formatStringSuggestion,
  number: formatNumericSuggestion,
  float: formatNumericSuggestion,
  array: formatArraySuggestion,
  object: formatObjectSuggestion,
  text: formatStringSuggestion,
  email: formatStringSuggestion,
  textarea: formatStringSuggestion,
};

/**
 * Formats a suggestion value for display based on the field type.
 * Handles various field types: text, date, select, multi-select, phone, etc.
 *
 * @param suggestion - The raw suggestion value from extracted_data
 * @param field - The field schema containing type and options
 * @returns Formatted string ready for display
 */
export function formatSuggestionValue(suggestion: any, field: FormField): string {
  // Handle null/undefined
  if (suggestion === null || suggestion === undefined) {
    return '';
  }

  const formatter = formatters[field.type] || formatStringSuggestion;
  return formatter(suggestion, field);
}
